// @ts-nocheck
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import siteMessage from "@insite/client-framework/SiteMessage";
import { getPageLinkByNodeId } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { HasFields } from "@insite/client-framework/Types/ContentItemModel";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Typography, { TypographyPresentationProps, TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import constants from "@store/constants";
import { blogFeaturedImageField, blogFeaturedTextField } from "@store/Framework/Common/Fields/Toolkit/blog";
import getTextAlignmentField, { textAlignmentOptions } from "@store/Framework/Common/Fields/Toolkit/textAlignment";
import BlogFeaturedCategoryList, {
    BlogFeaturedCategoryListPresentationProps,
} from "@store/Library/Components/Toolkit/Blog/BlogFeaturedCategoryList";
import { LoadingOverlayProps } from "@store/MobiusOverrides/LoadingOverlay/LoadingOverlay";
import { blogProps } from "@store/Themes/common/props/blog";
import { typographyGetNoResultsProps } from "@store/Themes/common/props/typography";
import cssProp from "@toolkit/Framework/Common/Utilities/cssProp";
import mergeProps from "@toolkit/Framework/Common/Utilities/mergeProps";
import { PageChildrenSelectorFieldValue } from "@toolkit/Framework/Types/FieldDefinition";
import * as React from "react";
import { FC, useMemo } from "react";
import { css } from "styled-components";

const enum fields {
    categories = "categories",
    readMoreLabel = "readMoreLabel",
    subheading = "subheading",
    title = "title",
    titleAlignment = "titleAlignment",
}

const enum categoriesFields {
    id = "id",
    image = "image",
    page = "page",
    text = "text",
}

const titleAlignmentField = getTextAlignmentField(fields.titleAlignment);

interface BlogHomeFeaturedCategoriesProps extends WidgetProps {
    fields: {
        [fields.categories]: [
            {
                fields: {
                    [categoriesFields.page]: PageChildrenSelectorFieldValue;
                    [categoriesFields.image]: string;
                    [categoriesFields.text]: string;
                };
            },
        ];
        [fields.readMoreLabel]: string;
        [fields.subheading]: string;
        [fields.title]: string;
        [fields.titleAlignment]: string;
    };
}

export interface BlogHomeFeaturedCategoriesStyles {
    title?: TypographyPresentationProps;
    loadingOverlay?: LoadingOverlayProps;
    noResultsContainer?: InjectableCss;
    noResultsText?: TypographyProps;
    subheading?: TypographyPresentationProps;
    blogFeaturedCategoryList?: BlogFeaturedCategoryListPresentationProps;
}

export const blogHomeFeaturedCategoriesStyles: BlogHomeFeaturedCategoriesStyles = {
    title: { variant: "h2" },
    noResultsText: typographyGetNoResultsProps(),
    subheading: blogProps.blockSubheading,
};

const styles = blogHomeFeaturedCategoriesStyles;

const BlogHomeFeaturedCategories: FC<BlogHomeFeaturedCategoriesProps> = ({ fields }) => {
    const { categories: baseCategories, readMoreLabel, subheading, title, titleAlignment: baseTitleAlignment } = fields;
    const titleAlignment = titleAlignmentField.parse(baseTitleAlignment);
    const categoriesPagesLink = useAppSelector(state =>
        baseCategories?.map(x => {
            return getPageLinkByNodeId(state, x.fields.page.value);
        }),
    );
    const categories = baseCategories.map((x, index) => {
        return {
            id: x.fields.page.value,
            image: x.fields.image,
            text: x.fields.text,
            title: categoriesPagesLink[index]?.title,
            url: categoriesPagesLink[index]?.url,
        };
    });

    const titleStyles = useMemo(() => {
        return mergeProps<BlogHomeFeaturedCategoriesStyles["title"]>(styles.title, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.title, titleAlignment]);

    const noResultsTextStyles = useMemo(() => {
        return mergeProps<BlogHomeFeaturedCategoriesStyles["noResultsText"]>(styles.noResultsText, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.noResultsText, titleAlignment]);

    const subheadingStyles = useMemo(() => {
        return mergeProps<BlogHomeFeaturedCategoriesStyles["subheading"]>(styles.subheading, {
            css: css`
                ${cssProp("text-align", titleAlignment)}
            `,
        });
    }, [styles.subheading, titleAlignment]);

    return (
        <>
            <Typography {...titleStyles}>{title}</Typography>
            {!categoriesPagesLink?.length ? (
                <StyledWrapper {...styles.noResultsContainer}>
                    <Typography {...noResultsTextStyles}>{siteMessage("Blog_Featured_Categries_Not_Found")}</Typography>
                </StyledWrapper>
            ) : (
                <>
                    {subheading ? <Typography {...subheadingStyles}>{subheading}</Typography> : null}
                    <BlogFeaturedCategoryList
                        {...styles.blogFeaturedCategoryList}
                        categories={categories}
                        readMoreLabel={readMoreLabel}
                    />
                </>
            )}
        </>
    );
};

const widgetModule: WidgetModule<Toolkit.FieldDefinition> = {
    component: BlogHomeFeaturedCategories,
    definition: {
        group: constants.widgetGroup.blog,
        allowedContexts: [constants.pageType.blogHomePage],
        displayName: "Featured Categories",
        fieldDefinitions: [
            {
                name: fields.title,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Featured Categories",
                isRequired: true,
            },
            {
                ...titleAlignmentField.definition,
                displayName: "Title Alignment",
                fieldType: "Contextual",
                defaultValue: textAlignmentOptions.center.id,
            },
            {
                name: fields.subheading,
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "",
            },
            {
                name: fields.readMoreLabel,
                displayName: "Read More Link Label",
                editorTemplate: "TextField",
                fieldType: "Contextual",
                defaultValue: "Discover More",
                isRequired: true,
            },
            {
                name: fields.categories,
                editorTemplate: "ListField",
                getDisplay: (item: HasFields, state) => {
                    const { value } = item.fields.page;
                    return getPageLinkByNodeId(state, value)?.title || translate("Category");
                },
                defaultValue: [],
                fieldType: "Contextual",
                tooltip: "For best visual effect, we recommend selecting a maximum of 6 categories.",
                fieldDefinitions: [
                    {
                        name: categoriesFields.id,
                        editorTemplate: "UniqueIdField",
                    },
                    {
                        name: categoriesFields.page,
                        displayName: "Category",
                        editorTemplate: "PageChildrenSelectorField",
                        isRequired: true,
                        defaultValue: {
                            value: "",
                            type: "Page",
                        },
                        showOnlyFirstLevelChildren: true,
                    },
                    {
                        ...blogFeaturedImageField,
                        name: categoriesFields.image,
                        displayName: "Image",
                        isRequired: true,
                        tooltip:
                            "For best visual effect, we recommend an image with a 4:3 aspect ratio and a size of at least 696px x 522px.",
                        tooltipPosition: "left",
                    },
                    {
                        ...blogFeaturedTextField,
                        name: categoriesFields.text,
                        displayName: "Text",
                        isRequired: true,
                        tooltip:
                            "For best visual effect, we recommend a text length of around 215 characters. If the text is too long, it will be cut off.",
                        tooltipPosition: "left",
                    },
                ],
            },
        ],
    },
};

export default widgetModule;
